import axios from "axios";
import { API_BASE_URL, x_staging_key } from "../config/env";
import * as Sentry from "@sentry/react";

const REQUEST_TIMEOUT_MS = 15000;

export const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  timeout: REQUEST_TIMEOUT_MS,
  headers: {
    Accept: "application/json",
    "X-Requested-With": "XMLHttpRequest",
    ...(x_staging_key && { "x-staging-key": x_staging_key }),
  },
});

const getPayloadMessages = (payload) => {
  if (!payload) {
    return [];
  }

  if (typeof payload === "string") {
    return payload.trim() ? [payload.trim()] : [];
  }

  if (Array.isArray(payload)) {
    return payload.flatMap(getPayloadMessages);
  }

  if (typeof payload === "object") {
    return Object.values(payload).flatMap(getPayloadMessages);
  }

  return [];
};

const normalizeApiError = (error) => {
  const status = error?.response?.status;
  const data = error?.response?.data;
  const isTimeoutError = error?.code === "ECONNABORTED";
  const isCanceledError = error?.code === "ERR_CANCELED";
  const isNetworkError = Boolean(error?.request && !error?.response);
  const payloadMessage = [
    data?.message,
    data?.error,
    data?.errors,
    data?.details,
    data?.validationErrors,
  ]
    .flatMap(getPayloadMessages)
    .find(Boolean);

  let message = payloadMessage;

  if (!message) {
    if (isTimeoutError) {
      message = "استغرق الطلب وقتًا أطول من المتوقع. يرجى المحاولة مرة أخرى.";
    } else if (isCanceledError) {
      message = "تم إلغاء الطلب.";
    } else if (isNetworkError) {
      message = "تعذر الاتصال بالخادم. يرجى التحقق من اتصال الإنترنت.";
    } else if (status === 401) {
      message = "انتهت صلاحية الجلسة. يرجى تسجيل الدخول مرة أخرى.";
    } else if (status === 403) {
      message = "ليس لديك صلاحية لتنفيذ هذا الإجراء.";
    } else if (status === 429) {
      message =
        "تم إرسال طلبات كثيرة. يرجى الانتظار قليلًا ثم المحاولة مرة أخرى.";
    } else if (status >= 500) {
      message = "حدث خطأ في الخادم. يرجى المحاولة لاحقًا.";
    } else {
      message = error?.message || "حدث خطأ غير متوقع في الخادم.";
    }
  }

  const normalizedError = new Error(message);

  normalizedError.name = "ApiError";
  normalizedError.status = status;
  normalizedError.code = error?.code;
  normalizedError.response = error?.response;
  normalizedError.request = error?.request;
  normalizedError.data = data;
  normalizedError.isAxiosError = Boolean(error?.isAxiosError);
  normalizedError.isNetworkError = isNetworkError;
  normalizedError.isTimeoutError = isTimeoutError;
  normalizedError.isCanceledError = isCanceledError;
  normalizedError.isAuthError = status === 401;
  normalizedError.originalError = error;

  // 👇 Add this before the return
  const shouldReport =
    !isCanceledError && // user navigated away, not a real error
    status !== 401 && // expected, session expired
    status !== 403 && // expected, unauthorized
    status !== 422; // expected, validation errors

  if (shouldReport) {
    Sentry.captureException(normalizedError, {
      extra: {
        status,
        url: error?.config?.url,
        method: error?.config?.method,
        data: error?.response?.data,
      },
    });
  }

  return normalizedError;
};

api.interceptors.response.use(
  (response) => response,
  (error) => Promise.reject(normalizeApiError(error)),
);
